import {
  computePointsAreaHa,
  computePointsPerimeterM,
  pathToPolygonWkt,
} from "./mapMeasurements";

function createManualPieceId() {
  return `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function elementsByLocalName(root, localName) {
  if (!root) return [];
  return Array.from(root.getElementsByTagName("*")).filter(
    (element) =>
      element.localName === localName ||
      element.tagName === localName ||
      element.tagName.endsWith(`:${localName}`)
  );
}

function firstElementByLocalName(root, localName) {
  const matches = elementsByLocalName(root, localName);
  return matches.length ? matches[0] : null;
}

export function parseCoordinatesText(text) {
  if (!text || !String(text).trim()) return [];

  const points = [];
  const tokens = String(text).trim().split(/\s+/);

  tokens.forEach((token) => {
    const parts = token.split(",").map((part) => part.trim());
    if (parts.length < 2) return;
    const lng = Number(parts[0]);
    const lat = Number(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
    points.push({ lat, lng });
  });

  if (points.length >= 2) {
    const first = points[0];
    const last = points[points.length - 1];
    if (
      Math.abs(first.lat - last.lat) < 0.000001 &&
      Math.abs(first.lng - last.lng) < 0.000001
    ) {
      points.pop();
    }
  }

  return points.length >= 3 ? points : [];
}

function polygonPointsFromElement(polygonElement) {
  const outerBoundary =
    firstElementByLocalName(polygonElement, "outerBoundaryIs") || polygonElement;
  const linearRing =
    firstElementByLocalName(outerBoundary, "LinearRing") || outerBoundary;
  const coordinates =
    firstElementByLocalName(linearRing, "coordinates") ||
    firstElementByLocalName(polygonElement, "coordinates");

  if (!coordinates) return [];
  return parseCoordinatesText(coordinates.textContent);
}

function polygonsFromPlacemark(placemark) {
  const name =
    (firstElementByLocalName(placemark, "name") || {}).textContent || "";
  const label = String(name).trim();
  const polygons = [];

  elementsByLocalName(placemark, "Polygon").forEach((polygonElement) => {
    const points = polygonPointsFromElement(polygonElement);
    if (points.length >= 3) {
      polygons.push({ name: label, points });
    }
  });

  elementsByLocalName(placemark, "MultiGeometry").forEach((multiGeometry) => {
    elementsByLocalName(multiGeometry, "Polygon").forEach((polygonElement) => {
      const points = polygonPointsFromElement(polygonElement);
      if (points.length >= 3) {
        polygons.push({ name: label, points });
      }
    });
  });

  return polygons;
}

export function extractPolygonsFromKmlText(kmlText) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(kmlText, "text/xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("Arquivo KML inválido.");
  }

  const polygons = [];
  elementsByLocalName(doc, "Placemark").forEach((placemark) => {
    polygons.push(...polygonsFromPlacemark(placemark));
  });

  if (!polygons.length) {
    elementsByLocalName(doc, "Polygon").forEach((polygonElement) => {
      const points = polygonPointsFromElement(polygonElement);
      if (points.length >= 3) {
        polygons.push({ name: "", points });
      }
    });
  }

  return polygons;
}

async function inflateDeflateRaw(compressedBytes) {
  const DecompressionStreamRef =
    typeof window !== "undefined" ? window.DecompressionStream : undefined;
  if (!DecompressionStreamRef) {
    throw new Error(
      "Seu navegador não suporta KMZ. Use um arquivo .kml ou outro navegador."
    );
  }

  const stream = new Blob([compressedBytes])
    .stream()
    .pipeThrough(new DecompressionStreamRef("deflate-raw"));
  const buffer = await new Response(stream).arrayBuffer();
  return new TextDecoder("utf-8").decode(buffer);
}

async function extractKmlFromKmz(arrayBuffer) {
  const view = new DataView(arrayBuffer);
  let offset = 0;

  while (offset + 30 <= arrayBuffer.byteLength) {
    if (view.getUint32(offset, true) !== 0x04034b50) {
      offset += 1;
      continue;
    }

    const compressionMethod = view.getUint16(offset + 8, true);
    const compressedSize = view.getUint32(offset + 18, true);
    const fileNameLength = view.getUint16(offset + 26, true);
    const extraFieldLength = view.getUint16(offset + 28, true);
    const nameStart = offset + 30;
    const nameBytes = new Uint8Array(arrayBuffer, nameStart, fileNameLength);
    const fileName = new TextDecoder("utf-8").decode(nameBytes).toLowerCase();
    const dataStart = nameStart + fileNameLength + extraFieldLength;
    const dataEnd = dataStart + compressedSize;

    if (dataEnd > arrayBuffer.byteLength) {
      break;
    }

    if (fileName.endsWith(".kml")) {
      const compressed = new Uint8Array(arrayBuffer, dataStart, compressedSize);
      if (compressionMethod === 0) {
        return new TextDecoder("utf-8").decode(compressed);
      }
      if (compressionMethod === 8) {
        return inflateDeflateRaw(compressed);
      }
      throw new Error("KMZ com compressão não suportada.");
    }

    offset = dataEnd;
  }

  throw new Error("Nenhum arquivo KML encontrado dentro do KMZ.");
}

export async function readKmlTextFromFile(file) {
  const fileName = String(file && file.name ? file.name : "").toLowerCase();
  if (fileName.endsWith(".kml")) {
    return file.text();
  }
  if (fileName.endsWith(".kmz")) {
    return extractKmlFromKmz(await file.arrayBuffer());
  }
  throw new Error("Use arquivos .kml ou .kmz.");
}

export function manualPiecesFromKmlPolygons(polygons, startIndex = 0) {
  return (polygons || [])
    .filter((polygon) => polygon.points && polygon.points.length >= 3)
    .map((polygon, index) => {
      const points = polygon.points.map((point) => ({ ...point }));
      return {
        id: createManualPieceId(),
        label: polygon.name || `Trecho ${startIndex + index + 1}`,
        points,
        areaHa: computePointsAreaHa(points),
        perimeterM: computePointsPerimeterM(points),
        polygonWkt: pathToPolygonWkt(points),
      };
    });
}

export async function importManualPiecesFromKmlFile(file, existingPieces = []) {
  const kmlText = await readKmlTextFromFile(file);
  const polygons = extractPolygonsFromKmlText(kmlText);
  if (!polygons.length) {
    throw new Error("Nenhum polígono encontrado no arquivo.");
  }

  const startIndex = Array.isArray(existingPieces) ? existingPieces.length : 0;
  const imported = manualPiecesFromKmlPolygons(polygons, startIndex);
  if (!imported.length) {
    throw new Error("Nenhum polígono válido encontrado no arquivo.");
  }

  return [...(existingPieces || []), ...imported];
}
